import React, { useCallback, useEffect, useMemo, useState } from "react";
import { useNavigate } from "react-router-dom";
import AuthService from "./AuthService";
import axiosInstance from "./axiosInstance";

function CustomNavbar({ onToggleSidebar }) {
  const navigate = useNavigate();
  const [user, setUser] = useState(() => AuthService.getCurrentUser());
  const [notifications, setNotifications] = useState([]);
  const [unreadCount, setUnreadCount] = useState(0);
  const [notificationsLoading, setNotificationsLoading] = useState(false);
  const [selectedNotification, setSelectedNotification] = useState(null);

  const refreshCurrentUser = useCallback(() => {
    const latestUser = AuthService.getCurrentUser();
    setUser(latestUser ? { ...latestUser } : null);
  }, []);

  useEffect(() => {
    const handleUserUpdated = (event) => {
      if (event?.detail?.user) {
        setUser({ ...event.detail.user });
      } else {
        refreshCurrentUser();
      }
    };

    window.addEventListener("storage", refreshCurrentUser);
    window.addEventListener("focus", refreshCurrentUser);
    window.addEventListener("grange:user-updated", handleUserUpdated);

    return () => {
      window.removeEventListener("storage", refreshCurrentUser);
      window.removeEventListener("focus", refreshCurrentUser);
      window.removeEventListener("grange:user-updated", handleUserUpdated);
    };
  }, [refreshCurrentUser]);

  const fullName =
    user?.full_name ||
    `${user?.first_name || ""} ${user?.last_name || ""}`.trim() ||
    "Admin User";

  const role = user?.role_name || user?.user_type || "ADMIN";
  const email = user?.email || "No email available";
  const profilePicture =
    user?.profile_picture ||
    user?.profile_picture_url ||
    user?.photo_url ||
    "";

  const initials = useMemo(() => {
    return fullName
      .split(" ")
      .filter(Boolean)
      .slice(0, 2)
      .map((name) => name[0]?.toUpperCase())
      .join("");
  }, [fullName]);

  const loginTime = useMemo(() => {
    const storedTime = localStorage.getItem("login_time");

    if (storedTime) {
      return new Date(storedTime).toLocaleString();
    }

    const now = new Date().toISOString();
    localStorage.setItem("login_time", now);
    return new Date(now).toLocaleString();
  }, []);

  const loadNotifications = useCallback(async () => {
    try {
      setNotificationsLoading(true);
      const response = await axiosInstance.get("notifications/");
      setNotifications(response.data?.notifications || []);
      setUnreadCount(response.data?.unread_count || 0);
    } catch (error) {
      console.error("Failed to load notifications", error);
    } finally {
      setNotificationsLoading(false);
    }
  }, []);

  useEffect(() => {
    if (!user) return undefined;
    loadNotifications();
    const timer = window.setInterval(loadNotifications, 60000);
    return () => window.clearInterval(timer);
  }, [loadNotifications, user]);

  const openNotification = async (item) => {
    setSelectedNotification(item);
    if (!item?.is_read) {
      try {
        await axiosInstance.post(`notifications/${item.id}/read/`);
        const updated = { ...item, is_read: true };
        setSelectedNotification(updated);
        setNotifications((current) =>
          current.map((notification) =>
            notification.id === item.id ? updated : notification
          )
        );
        setUnreadCount((count) => Math.max(0, count - 1));
      } catch (error) {
        console.error("Failed to mark notification as read", error);
      }
    }
  };

  const markAllNotificationsRead = async () => {
    try {
      await axiosInstance.post("notifications/mark-all-read/");
      setNotifications((current) =>
        current.map((notification) => ({ ...notification, is_read: true }))
      );
      setUnreadCount(0);
    } catch (error) {
      console.error("Failed to mark notifications as read", error);
    }
  };

  const handleLogout = () => {
    AuthService.logout();
    localStorage.removeItem("login_time");
    navigate("/", { replace: true });
  };

  const Avatar = ({ size }) => {
    const [imageFailed, setImageFailed] = useState(false);
    const showImage = Boolean(profilePicture) && !imageFailed;

    useEffect(() => {
      setImageFailed(false);
    }, [profilePicture]);

    return showImage ? (
      <img
        src={profilePicture}
        alt={fullName}
        onError={() => setImageFailed(true)}
        style={{
          width: size,
          height: size,
          borderRadius: "50%",
          objectFit: "cover",
        }}
      />
    ) : (
      <div
        className="d-flex align-items-center justify-content-center text-white fw-bold"
        style={{
          width: size,
          height: size,
          borderRadius: "50%",
          background: "#125b2d",
        }}
      >
        {initials || "AD"}
      </div>
    );
  };

  return (
    <>
    <header className="grange-navbar bg-white border-bottom px-4 py-3 d-flex align-items-center justify-content-between shadow-sm">
      <div className="d-flex align-items-center gap-3" style={{ minWidth: 0 }}>
        <button
          className="btn btn-light border d-flex align-items-center justify-content-center"
          onClick={onToggleSidebar}
          style={{ width: 42, height: 42, borderRadius: 12, flexShrink: 0 }}
          title="Toggle sidebar"
          aria-label="Toggle navigation menu"
          type="button"
        >
          <i className="bi bi-list fs-5"></i>
        </button>

        <div className="grange-navbar-title">
          <h6 className="mb-0 fw-bold">School Management Dashboard</h6>
          <small className="text-muted">
            Manage academics, communication and school operations
          </small>
        </div>
      </div>

      <div className="d-flex align-items-center gap-3">
        <div className="dropdown">
          <button
            className="btn btn-light border position-relative d-flex align-items-center justify-content-center"
            style={{ width: 42, height: 42, borderRadius: 12 }}
            title="Notifications"
            type="button"
            data-bs-toggle="dropdown"
            aria-expanded="false"
            onClick={loadNotifications}
          >
            <i className="bi bi-bell"></i>
            {unreadCount > 0 && (
              <span
                className="position-absolute translate-middle badge rounded-pill bg-danger"
                style={{ top: 3, right: -9, fontSize: 10 }}
              >
                {unreadCount > 99 ? "99+" : unreadCount}
              </span>
            )}
          </button>

          <div
            className="dropdown-menu dropdown-menu-end shadow border-0 p-0 overflow-hidden"
            style={{ width: 380, maxWidth: "92vw", borderRadius: 16 }}
          >
            <div className="p-3 border-bottom d-flex justify-content-between align-items-center">
              <div>
                <div className="fw-bold">Notifications</div>
                <small className="text-muted">{unreadCount} unread</small>
              </div>
              {unreadCount > 0 && (
                <button
                  type="button"
                  className="btn btn-sm btn-link text-success text-decoration-none"
                  onClick={markAllNotificationsRead}
                >
                  Mark all read
                </button>
              )}
            </div>

            <div style={{ maxHeight: 420, overflowY: "auto" }}>
              {notificationsLoading && notifications.length === 0 ? (
                <div className="p-4 text-center text-muted">Loading notifications...</div>
              ) : notifications.length === 0 ? (
                <div className="p-4 text-center text-muted">No notifications yet.</div>
              ) : (
                notifications.map((item) => (
                  <button
                    type="button"
                    key={item.id}
                    className={`dropdown-item text-wrap border-bottom p-3 ${!item.is_read ? "bg-light" : ""}`}
                    onClick={() => openNotification(item)}
                  >
                    <div className="d-flex justify-content-between gap-2">
                      <strong className="small">{item.title}</strong>
                      {!item.is_read && <span className="badge bg-success">New</span>}
                    </div>
                    <div className="small text-muted mt-1" style={{ whiteSpace: "normal" }}>
                      {item.message?.length > 120 ? `${item.message.slice(0, 120)}...` : item.message}
                    </div>
                    <div className="d-flex flex-wrap gap-2 mt-2">
                      {item.created_by && <small className="text-muted">From: {item.created_by}</small>}
                      {item.attachment_url && (
                        <a
                          href={item.attachment_url}
                          target="_blank"
                          rel="noreferrer"
                          className="small text-success"
                          onClick={(event) => event.stopPropagation()}
                        >
                          <i className="bi bi-paperclip me-1"></i>
                          {item.attachment_name || "Attachment"}
                        </a>
                      )}
                    </div>
                  </button>
                ))
              )}
            </div>
          </div>
        </div>

        <div className="dropdown">
          <button
            className="btn border bg-white d-flex align-items-center justify-content-center p-1"
            type="button"
            data-bs-toggle="dropdown"
            aria-expanded="false"
            title={fullName}
            style={{ width: 50, height: 50, borderRadius: "50%" }}
          >
            <Avatar size={42} />
          </button>

          <div
            className="dropdown-menu dropdown-menu-end shadow border-0 p-0 overflow-hidden"
            style={{ minWidth: 320, borderRadius: 16 }}
          >
            <div className="p-3" style={{ background: "#f3f8f4" }}>
              <div className="d-flex align-items-center gap-3">
                <Avatar size={52} />

                <div style={{ minWidth: 0 }}>
                  <div className="fw-bold text-truncate">{fullName}</div>
                  <small className="text-muted text-truncate d-block">
                    {email}
                  </small>
                </div>
              </div>
            </div>

            <div className="p-3">
              <div className="d-flex justify-content-between align-items-center mb-2">
                <span className="text-muted small">Role</span>
                <strong className="small">{role}</strong>
              </div>

              <div className="d-flex justify-content-between align-items-start mb-3">
                <span className="text-muted small">Logged in</span>
                <strong className="small text-end ms-3">{loginTime}</strong>
              </div>

              <button
                className="dropdown-item rounded-3 py-2"
                onClick={() => {
                  if (user?.user_type === "TEACHER") navigate("/teacher/profile");
                  else if (user?.user_type === "PARENT") navigate("/parent/profile");
                  else navigate("/profile");
                }}
                type="button"
              >
                <i className="bi bi-person me-2"></i>
                Profile
              </button>

              {["ADMIN", "SUPER_ADMIN"].includes(user?.user_type) && (
                <button
                  className="dropdown-item rounded-3 py-2"
                  onClick={() => navigate("/settings")}
                  type="button"
                >
                  <i className="bi bi-gear me-2"></i>
                  Settings
                </button>
              )}

              <hr />

              <button
                className="dropdown-item rounded-3 py-2 text-danger fw-bold"
                onClick={handleLogout}
                type="button"
              >
                <i className="bi bi-box-arrow-right me-2"></i>
                Logout
              </button>
            </div>
          </div>
        </div>
      </div>
    </header>

    {selectedNotification && (
      <div
        className="modal fade show d-block"
        tabIndex="-1"
        style={{ background: "rgba(0,0,0,0.45)" }}
      >
        <div className="modal-dialog modal-lg modal-dialog-scrollable">
          <div className="modal-content border-0 shadow">
            <div className="modal-header">
              <div>
                <h5 className="modal-title fw-bold">{selectedNotification.title}</h5>
                <small className="text-muted">
                  {selectedNotification.created_by ? `From ${selectedNotification.created_by}` : "School communication"}
                </small>
              </div>
              <button
                type="button"
                className="btn-close"
                onClick={() => setSelectedNotification(null)}
              ></button>
            </div>
            <div className="modal-body">
              <div className="d-flex flex-wrap gap-2 mb-3">
                <span className="badge text-bg-light border">{selectedNotification.priority}</span>
                {selectedNotification.audience && (
                  <span className="badge text-bg-light border">{selectedNotification.audience}</span>
                )}
              </div>
              <div className="border rounded-3 bg-light p-3" style={{ whiteSpace: "pre-wrap" }}>
                {selectedNotification.message}
              </div>
              {selectedNotification.attachment_url && (
                <a
                  href={selectedNotification.attachment_url}
                  target="_blank"
                  rel="noreferrer"
                  className="btn btn-outline-success mt-3"
                >
                  <i className="bi bi-paperclip me-2"></i>
                  Open {selectedNotification.attachment_name || "attachment"}
                </a>
              )}
            </div>
            <div className="modal-footer">
              <button
                type="button"
                className="btn btn-secondary"
                onClick={() => setSelectedNotification(null)}
              >
                Close
              </button>
            </div>
          </div>
        </div>
      </div>
    )}
    </>
  );
}

export default CustomNavbar;
